import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import sha256 from 'crypto-js/sha256';
import { once, run, oncePromise } from '../../helper/electron';
import {
  FilePathMap,
  iLocalFile,
  iLocalFileAPIResponse,
} from '../../types/ILocalFile';
import { iTrack } from '../../types/ITrack';
import type { AppDispatch, RootState } from '../types';
import { normalizePath } from '../../utils/normalizePath';
import { toDurationMs } from '../../utils/toDurationMs';
import { mergePlayCounts } from '../../utils/mergePlayCounts';
import { mergeTrackMatchFiles } from '../../utils/mergeTrackMatchFiles';

const SCAN_TRACK_TIMEOUT_MS = 10000;
const TRACK_MATCH_TIMEOUT_MS = 15000;
const SAVED_FILE_PATHS_TIMEOUT_MS = 30000;

// Enhanced scoring interfaces
interface DetailedMatchScores {
  overall: number;
  artistScore: number;
  titleScore: number;
  bpmScore: number;
  durationScore?: number;
  filenameScore?: number;
  confidence: 'high' | 'medium' | 'low';
  reasons?: string[];
}

interface EnhancedLocalFile extends iLocalFile {
  detailed_scores?: DetailedMatchScores;
  confidence?: 'high' | 'medium' | 'low';
  match_score?: number;
}

interface TrackMatcherState {
  showMatcher: boolean;
  trackMatches: {
    [trackId: string]: EnhancedLocalFile[];
  };
  trackMatcherCount: number;
  selectedMatchesFiles: {
    [trackId: string]: EnhancedLocalFile[];
  };
  selectedMatches: FilePathMap;
  currentRequests: {
    [trackId: string]: string | null;
  };
  bulkProgress: {
    total: number;
    completed: number;
    mode?: 'first' | 'all' | 'none' | 'mostPlayed';
    active: boolean;
  };
}
type ProgressCallback = (
  current: number,
  total: number,
  message?: string,
) => void;

const initialState: TrackMatcherState = {
  showMatcher: true,
  trackMatches: {},
  trackMatcherCount:
    Number(JSON.parse(localStorage.getItem('trackMatcherCount') as string)) ||
    10,
  selectedMatchesFiles: {},
  selectedMatches: {},
  currentRequests: {},
  bulkProgress: { total: 0, completed: 0, active: false },
};

const removeUndefined = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined); // Recursively process array elements
  } else if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, removeUndefined(value)]), // Recursively process nested objects
    );
  }
  return obj;
};

const parseTags = (files: iLocalFileAPIResponse[]): EnhancedLocalFile[] => {
  return files.map((file: any): EnhancedLocalFile => {
    let tags: any = {};
    try {
      tags =
        typeof file.tags === 'string'
          ? JSON.parse(file.tags || '{}')
          : file.tags || {};
    } catch (e) {
      console.warn('Failed to parse tags for file:', file.filePath);
      tags = {};
    }

    const enhancedFile: EnhancedLocalFile = {
      id: file.id,
      filePath: normalizePath(file.filePath),
      tags,
      isSelected: file.isSelected,
      duration_ms: file.duration_ms,
    };

    // Add enhanced scoring information if available
    if (file.detailed_scores) {
      enhancedFile.detailed_scores = file.detailed_scores;
    }
    if (file.confidence) {
      enhancedFile.confidence = file.confidence;
    }
    if (file.match_score !== undefined) {
      enhancedFile.match_score = file.match_score;
    }

    return enhancedFile;
  });
};

/**
 * Retrieves track matches from local computer via Electron
 */
export const addFile = createAsyncThunk<
  { file: EnhancedLocalFile; trackId: string },
  { file: EnhancedLocalFile; trackId: string; crateId?: string }
>('trackMatcher/addTrack', async ({ file, trackId, crateId }, thunkAPI) => {
  try {
    run('my-music/scan-track', { track: file });
    const newFile = await new Promise<iLocalFile>((resolve, reject) => {
      let settled = false;

      const timeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error(`Timed out scanning local file: ${file.filePath}`));
      }, SCAN_TRACK_TIMEOUT_MS);

      once(`my-music/scan-track:${file.id}`, (data) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        resolve(data);
      });
    });

    const scanError = (newFile as { error?: string } | undefined)?.error;
    if (!newFile?.filePath || scanError) {
      return thunkAPI.rejectWithValue(
        scanError || `Could not add local file: ${file.filePath}`,
      );
    }

    const parsedFile = parseTags([newFile as iLocalFileAPIResponse])[0];
    const cleanedParsedFile = removeUndefined(parsedFile) as EnhancedLocalFile;

    cleanedParsedFile.isSelected = true;

    const state = thunkAPI.getState() as RootState;
    const existingMatches = state.trackMatcherState.trackMatches[trackId] || [];
    const currentSelectedMatches =
      state.trackMatcherState.selectedMatches[trackId] || [];
    const selectedPaths = [
      ...currentSelectedMatches,
      cleanedParsedFile.filePath,
    ];

    thunkAPI.dispatch(
      setSelectedFilePaths({
        trackId,
        filePaths: selectedPaths,
      }),
    );
    thunkAPI.dispatch(
      setTrackMatches({
        trackId,
        files: mergeTrackMatchFiles(existingMatches, [cleanedParsedFile]),
      }),
    );

    const targetCrateId = crateId ?? state.crate?.selectedCrate?._id;
    if (targetCrateId) {
      await thunkAPI.dispatch(
        saveSelectedTrackFilePaths({
          crateId: targetCrateId,
          trackId,
          filePaths: selectedPaths,
        }),
      );
    }

    return thunkAPI.fulfillWithValue({ file: cleanedParsedFile, trackId });
  } catch (err) {
    return thunkAPI.rejectWithValue((err as Error).toString());
  }
});

export const preloadAllTrackMatches = createAsyncThunk<
  void,
  {
    tracks: iTrack[];
    count: number;
    crateId: string;
    onProgress?: ProgressCallback;
  },
  { state: RootState; dispatch: AppDispatch }
>(
  'trackMatcher/preloadAllTrackMatches',
  async ({ tracks, count, crateId, onProgress }, { dispatch, getState }) => {
    const BATCH_SIZE = 5;
    let loadedCount = 0;
    let successCount = 0;

    for (let i = 0; i < tracks.length; i += BATCH_SIZE) {
      const batch = tracks.slice(i, i + BATCH_SIZE);

      if (onProgress) {
        onProgress(
          loadedCount,
          tracks.length,
          `Loading batch ${Math.floor(i / BATCH_SIZE) + 1}...`,
        );
      }

      const batchPromises = batch.map(async (track, batchIndex) => {
        // IMPORTANT: Must use trimmed values to match hash generation in other parts of the app
        const trackId = sha256(
          JSON.stringify({
            artist: track.Artist?.trim() ?? '',
            title: track.Title?.trim() ?? '',
          }),
        ).toString();

        try {
          const currentTrack = i + batchIndex;
          if (onProgress) {
            onProgress(
              currentTrack,
              tracks.length,
              `${track.Artist} - ${track.Title}`,
            );
          }
          const response = await dispatch(
            getTrackMatches({
              id: trackId,
              artist: track.Artist,
              title: track.Title,
              count,
              bpm: track.BPM,
              durationMs: toDurationMs(track.duration),
            }),
          ).unwrap();

          if (response && response.length > 0) {
            dispatch(setTrackMatches({ trackId, files: response }));
            successCount++;
          }

          loadedCount++;

          if (onProgress) {
            onProgress(
              loadedCount,
              tracks.length,
              `Loaded ${loadedCount}/${tracks.length}`,
            );
          }
        } catch (error) {
          console.warn(
            `Failed to preload matches for ${track.Artist} - ${track.Title}`,
            error,
          );
          loadedCount++;
        }
      });
      await Promise.all(batchPromises);
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  },
);
/**
 * Retrieves track matches from local computer via Electron with enhanced scoring
 */
export const getTrackMatches = createAsyncThunk<
  iLocalFileAPIResponse[] | null,
  {
    artist: string;
    title: string;
    id: string;
    count?: number;
    bpm?: number;
    durationMs?: number;
  },
  {
    state: {
      trackMatcherState: {
        currentRequests: {
          [trackId: string]: string | null;
        };
      };
    };
  }
>(
  'trackMatcher/getTrackMatches',
  async ({ artist, title, id, count = 10, bpm, durationMs }, thunkAPI) => {
    if (
      thunkAPI.requestId !==
      thunkAPI.getState().trackMatcherState.currentRequests[id]
    ) {
      return null;
    }

    try {
      const vdjCacheEnabled =
        localStorage.getItem('vdjCacheEnabled') === 'true';
      run('getTrackMatches', {
        artist,
        title,
        id,
        count,
        bpm,
        durationMs,
        vdjCacheEnabled,
      });
      const response = await new Promise<iLocalFile[]>((resolve, reject) => {
        let settled = false;
        const timeout = setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(
            new Error(
              `Timed out searching local files for ${artist} - ${title}`,
            ),
          );
        }, TRACK_MATCH_TIMEOUT_MS);

        once(`getTrackMatches:${id}`, (data) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          resolve(data);
        });
      });
      return thunkAPI.fulfillWithValue(response);
    } catch (err) {
      return thunkAPI.rejectWithValue((err as Error).toString());
    }
  },
);

export const processSelectedFiles = createAsyncThunk<
  EnhancedLocalFile[],
  {
    crateId: string;
    trackId: string;
    filePaths: string[];
    onProgress?: (current: number, total: number) => void;
  },
  {
    state: {
      trackMatcherState: {
        currentRequests: {
          [key: string]: string | null;
        };
      };
    };
  }
>(
  'trackMatcher/processSelectedFiles',
  async ({ crateId, trackId, filePaths, onProgress }, thunkAPI) => {
    try {
      const fileObjects: EnhancedLocalFile[] = [];
      const total = filePaths.length;
      for (let i = 0; i < filePaths.length; i++) {
        const filePath = filePaths[i];
        const normalized = normalizePath(filePath);
        if (onProgress) {
          onProgress(i, total);
        }
        const fileId = sha256(normalized).toString();
        const uniqueEventName = `my-music/parse-track:${fileId}`;
        run('my-music/parse-track', { track: normalized });
        let tags: any = {};
        try {
          tags = await new Promise<any>((resolve, reject) => {
            let settled = false;

            const timeout = setTimeout(() => {
              if (settled) return;
              settled = true;
              console.warn(
                `[processSelectedFiles] Tag read timed out for: ${normalized}`,
              );
              reject(new Error(`Timeout waiting for tags from ${normalized}`));
            }, 10000);

            once(uniqueEventName, (data) => {
              if (settled) return;
              settled = true;
              clearTimeout(timeout);
              resolve(data);
            });
          });
        } catch (tagErr) {
          console.warn(
            `[processSelectedFiles] Could not read tags for "${normalized}", continuing with empty tags.`,
            tagErr,
          );
        }

        const newTrack: EnhancedLocalFile = {
          filePath: normalized,
          id: new Date().getTime() * filePath.length,
          isSelected: true,
          tags,
        };
        const parsedFile = parseTags([newTrack])[0];
        const cleanedParsedFile = removeUndefined(
          parsedFile,
        ) as EnhancedLocalFile;
        fileObjects.push(cleanedParsedFile);
        if (onProgress) {
          onProgress(i + 1, total);
        }
      }
      return thunkAPI.fulfillWithValue(fileObjects);
    } catch (err) {
      return thunkAPI.rejectWithValue((err as Error).toString());
    }
  },
);

/* Retrieves all saved  filePaths*/

export const getAllSavedFilePaths = createAsyncThunk<
  FilePathMap,
  { crateId: string; trackIds: string[] }
>(
  'trackMatcher/getAllSavedFilePaths',
  async ({ crateId, trackIds }, thunkAPI) => {
    try {
      run('crates/all-file-paths', { crateId, trackIds });
      const response = await new Promise<FilePathMap>((resolve, reject) => {
        let settled = false;
        const timeout = setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(
            new Error(
              `Timed out loading saved file paths for crate ${crateId}`,
            ),
          );
        }, SAVED_FILE_PATHS_TIMEOUT_MS);

        once(`crates/all-file-paths:${crateId}`, (data) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          resolve(data);
        });
      });
      return thunkAPI.fulfillWithValue(response);
    } catch (err) {
      return thunkAPI.rejectWithValue((err as Error).toString());
    }
  },
);

/**
 * Retrieves saved selected track matches (file paths) from local db.
 */
export const getSavedSelectedTrackMatches = createAsyncThunk<
  string[] | null,
  { crateId: string; trackId: string },
  {
    state: {
      trackMatcherState: {
        currentRequests: {
          [trackId: string]: string | null;
        };
      };
    };
  }
>(
  'trackMatcher/getSavedSelectedTrackMatches',
  async ({ crateId, trackId }, thunkAPI) => {
    // Ensure the request ID matches the current request stored in the state
    if (
      thunkAPI.requestId !==
      thunkAPI.getState().trackMatcherState.currentRequests[
        `${crateId}-${trackId}`
      ]
    ) {
      return null;
    }

    try {
      // Invoke the Electron process using the specific run string for selected track matches
      run('tracks/get-selected-file-paths', { crateId, trackId });

      // Wait for the response event from Electron using the unique id to match the request
      const response = await new Promise<string[]>((resolve) =>
        once(`tracks/get-selected-file-paths:${crateId}-${trackId}`, (data) => {
          resolve(data);
        }),
      );
      return thunkAPI.fulfillWithValue(response);
    } catch (err) {
      return thunkAPI.rejectWithValue((err as Error).toString());
    }
  },
);

export const saveSelectedTrackFilePaths = createAsyncThunk<
  void,
  { crateId: string; trackId: string; filePaths: string[] }
>(
  'trackMatcher/saveSelectedTrackFilePaths',
  async ({ crateId, trackId, filePaths }) => {
    run('crates/save-track-file-paths', {
      crate_id: crateId,
      filePaths: { [trackId]: filePaths },
    });
  },
);

export const trackMatcherSlice = createSlice({
  name: 'trackMatcher',
  initialState,
  reducers: {
    setShowMatcher: (state, action: PayloadAction<boolean>) => {
      state.showMatcher = action.payload;
    },
    setTrackMatcherCount: (state, action: PayloadAction<number>) => {
      state.trackMatcherCount = action.payload;
    },

    setBulkProgress: (
      state,
      action: PayloadAction<{
        total: number;
        completed: number;
        mode?: 'first' | 'all' | 'none' | 'mostPlayed';
        active: boolean;
      }>,
    ) => {
      state.bulkProgress = action.payload;
    },

    setTrackMatches: (
      state,
      action: PayloadAction<{
        trackId: string;
        files: iLocalFileAPIResponse[] | undefined;
      }>,
    ) => {
      const { trackId, files } = action.payload;
      if (!files) {
        delete state.trackMatches[trackId];
        return;
      }
      // Preserve isSelected for already-selected filePaths
      // Normalize paths in prevSelected for comparison
      const prevSelected = new Set(
        (state.selectedMatches[trackId] || []).map((path) =>
          normalizePath(path),
        ),
      );
      const parsedFiles = parseTags(files).map((f) => {
        const normalizedPath = normalizePath(f.filePath);
        return {
          ...f,
          isSelected: prevSelected.has(normalizedPath),
        };
      });

      state.trackMatches[trackId] = parsedFiles;
    },
    setSelectedFilePaths: (
      state,
      action: PayloadAction<{
        trackId: string;
        filePaths: string[];
      }>,
    ) => {
      state.selectedMatches[action.payload.trackId] = action.payload.filePaths;
    },
    clearTrackMatches: (state) => {
      state.selectedMatches = {};
      state.trackMatches = {};
    },
    // Drop loaded match rows so in-view tracks refetch, but keep checkbox
    // selections. Used when the VDJ cache index becomes ready.
    invalidateLoadedMatches: (state) => {
      state.trackMatches = {};
    },
    setSelectedTrackMatches: (
      state,
      action: PayloadAction<{
        trackId: string;
        matchId: number;
        isSelected: boolean;
      }>,
    ) => {
      const { trackId, matchId, isSelected } = action.payload;
      const updatedMatches =
        state.trackMatches[trackId]?.map((match) =>
          match.id === matchId ? { ...match, isSelected } : match,
        ) ?? [];

      state.trackMatches[trackId] = updatedMatches;

      const selectedPaths = updatedMatches
        .filter((match) => match.isSelected && match.filePath.length > 0)
        .map((match) => normalizePath(match.filePath));

      state.selectedMatches[trackId] = selectedPaths;
    },
    selectFirstMatches: (state) => {
      Object.entries(state.trackMatches).forEach(([trackId, files]) => {
        if (files.length > 0) {
          state.trackMatches[trackId] = files.map((f, i) => ({
            ...f,
            isSelected: i === 0,
          }));
          state.selectedMatches[trackId] = [normalizePath(files[0].filePath)];
        }
      });
    },
    selectMostPlayedMatches: (
      state,
      action: PayloadAction<Record<string, { playCount: number }>>,
    ) => {
      const playCountsMap = action.payload || {};
      const normalizedPlayCountsMap: Record<string, { playCount: number }> = {};
      Object.entries(playCountsMap).forEach(([key, value]) => {
        normalizedPlayCountsMap[normalizePath(key).toLowerCase()] = value;
      });

      console.log(
        `[selectMostPlayedMatches] Received ${Object.keys(playCountsMap).length} play counts from DJ platforms.`,
      );

      const getPlayCount = (filePath: string) => {
        const pathLower = normalizePath(filePath).toLowerCase();
        const fileName = pathLower.split('/').pop() || '';
        const baseName = fileName.replace(/\.[^/.]+$/, '');

        const cleanBase = baseName
          .replace(/\s*\d+$/, '')
          .replace(/\s*copy$/i, '')
          .trim();

        let totalPlays = 0;
        const seenKeys = new Set<string>();

        if (normalizedPlayCountsMap[pathLower]) {
          totalPlays += normalizedPlayCountsMap[pathLower].playCount;
          seenKeys.add(pathLower);
        }

        if (cleanBase) {
          Object.entries(normalizedPlayCountsMap).forEach(([k, entry]) => {
            if (seenKeys.has(k)) return;

            const kFileName = k.split('/').pop() || '';
            const kBaseName = kFileName.replace(/\.[^/.]+$/, '');
            const kCleanBase = kBaseName
              .replace(/\s*\d+$/, '')
              .replace(/\s*copy$/i, '')
              .trim();

            if (kCleanBase === cleanBase) {
              totalPlays += entry.playCount;
              seenKeys.add(k);
            }
          });
        }

        return totalPlays;
      };

      Object.entries(state.trackMatches).forEach(([trackId, files]) => {
        if (files.length > 0) {
          files.forEach((f) => {
            const pCount = getPlayCount(f.filePath);
          });

          const sorted = [...files].sort((a, b) => {
            const countA = getPlayCount(a.filePath);
            const countB = getPlayCount(b.filePath);

            if (countB !== countA) {
              return countB - countA;
            }

            const scoreA = a.match_score || 0;
            const scoreB = b.match_score || 0;
            return scoreB - scoreA;
          });

          const mostPlayedMatch = sorted[0];
          const topPlayCount = getPlayCount(mostPlayedMatch.filePath);

          if (topPlayCount > 0) {
            state.trackMatches[trackId] = files.map((f) => ({
              ...f,
              isSelected: f.id === mostPlayedMatch.id,
            }));
            state.selectedMatches[trackId] = [
              normalizePath(mostPlayedMatch.filePath),
            ];
          } else {
            state.trackMatches[trackId] = files.map((f) => ({
              ...f,
              isSelected: false,
            }));
            state.selectedMatches[trackId] = [];
          }
        }
      });
    },
    selectAllMatches: (state) => {
      Object.entries(state.trackMatches).forEach(([trackId, files]) => {
        state.trackMatches[trackId] = files.map((f) => ({
          ...f,
          isSelected: true,
        }));
        state.selectedMatches[trackId] = files.map((f) =>
          normalizePath(f.filePath),
        );
      });
    },
    selectNoMatches: (state) => {
      Object.entries(state.trackMatches).forEach(([trackId, files]) => {
        state.trackMatches[trackId] = files.map((f) => ({
          ...f,
          isSelected: false,
        }));
      });
      Object.keys(state.selectedMatches).forEach((trackId) => {
        state.selectedMatches[trackId] = [];
      });
    },
    removeTrackFromState: (state, action: PayloadAction<string>) => {
      const trackId = action.payload;
      delete state.trackMatches[trackId];
      delete state.selectedMatches[trackId];
      delete state.selectedMatchesFiles[trackId];
      delete state.currentRequests[trackId];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTrackMatches.fulfilled, (state, action) => {
      if (action.payload) {
        state.currentRequests[action.meta.arg.id] = null;
      }
    });
    builder.addCase(getTrackMatches.pending, (state, action) => {
      if (!state.currentRequests[action.meta.arg.id]) {
        state.currentRequests[action.meta.arg.id] = action.meta.requestId;
      }
    });
    builder.addCase(getTrackMatches.rejected, (state, action) => {
      state.currentRequests[action.meta.arg.id] = null;
    });

    builder.addCase(getSavedSelectedTrackMatches.fulfilled, (state, action) => {
      if (action.payload) {
        const { crateId, trackId } = action.meta.arg;
        const requestKey = `${crateId}-${trackId}`;
        // Update the selectedMatches directly with the file paths returned from Electron.
        state.selectedMatches[trackId] = action.payload;
        state.currentRequests[requestKey] = null;
      }
    });

    builder.addCase(processSelectedFiles.fulfilled, (state, action) => {
      const { trackId } = action.meta.arg;
      // Only proceed if trackId exists
      if (trackId) {
        const requestKey = `process-${action.meta.arg.crateId}-${trackId}`;
        state.selectedMatchesFiles[trackId] = action.payload;

        state.trackMatches[trackId] = mergeTrackMatchFiles(
          state.trackMatches[trackId] || [],
          action.payload,
        ) as EnhancedLocalFile[];
        // Clear the current request
        state.currentRequests[requestKey] = null;
      } else {
        console.warn('Attempted to process files without a valid trackId');
      }
    });
    builder.addCase(getSavedSelectedTrackMatches.pending, (state, action) => {
      if (
        !state.currentRequests[
          `${action.meta.arg.crateId}-${action.meta.arg.trackId}`
        ]
      ) {
        state.currentRequests[
          `${action.meta.arg.crateId}-${action.meta.arg.trackId}`
        ] = action.meta.requestId;
      }
    });
    builder.addCase(getSavedSelectedTrackMatches.rejected, (state, action) => {
      state.currentRequests[
        `${action.meta.arg.crateId}-${action.meta.arg.trackId}`
      ] = null;
    });
  },
});

export const {
  setShowMatcher,
  setTrackMatcherCount,
  setTrackMatches,
  setBulkProgress,
  setSelectedTrackMatches,
  selectFirstMatches,
  selectAllMatches,
  selectNoMatches,
  selectMostPlayedMatches,
  clearTrackMatches,
  invalidateLoadedMatches,
  setSelectedFilePaths,
  removeTrackFromState,
} = trackMatcherSlice.actions;

// Memoized selectors
export const selectShowMatcher = (state: RootState) =>
  state.trackMatcherState.showMatcher;
export const selectBulkProgress = (state: RootState) =>
  state.trackMatcherState.bulkProgress;
export const selectTrackMatchesCount = (state: RootState) =>
  state.trackMatcherState.trackMatcherCount;

const selectTrackMatcherState = (state: RootState) => state.trackMatcherState;

// Single memoized selector for all selected matches (returns the object reference)
export const selectAllSelectedMatches = createSelector(
  [selectTrackMatcherState],
  (state: TrackMatcherState) => state.selectedMatches,
);

// For backward compatibility - returns the same memoized selector
export const selectSelectedMatches = () => selectAllSelectedMatches;

// Memoized selector for all track matches (returns the object reference)
export const selectAllTrackMatches = createSelector(
  [selectTrackMatcherState],
  (state: TrackMatcherState) => state.trackMatches,
);

// Non-memoized helper - use selectAllTrackMatches in components instead
export const selectTrackMatches = (
  state: { trackMatcherState: TrackMatcherState },
  trackId: string,
): EnhancedLocalFile[] | undefined => {
  return state.trackMatcherState.trackMatches?.[trackId];
};

export const selectIsTrackLoadingMatches = (
  state: { trackMatcherState: TrackMatcherState },
  trackId: string,
): boolean => !!state.trackMatcherState.currentRequests[trackId];

const activeBulkOperations = new Set<string>();

export const bulkLoadAndSelect = createAsyncThunk<
  void,
  {
    crateId: string;
    tracks: iTrack[];
    mode: 'first' | 'all' | 'none' | 'mostPlayed';
    seratoPlayCounts?: Record<string, { playCount: number }>;
  },
  { state: RootState; dispatch: AppDispatch }
>(
  'trackMatcher/bulkLoadAndSelect',
  async (
    { crateId, tracks, mode, seratoPlayCounts },
    { dispatch, getState },
  ) => {
    dispatch(
      setBulkProgress({
        total: tracks.length,
        completed: 0,
        mode,
        active: true,
      }),
    );
    const operationKey = `${crateId}-${mode}`;

    if (activeBulkOperations.has(operationKey)) return;
    activeBulkOperations.add(operationKey);

    try {
      const matcher = getState().trackMatcherState;
      const BATCH_SIZE = 5;

      const matchesToUpdate: { [trackId: string]: iLocalFileAPIResponse[] } =
        {};

      for (let i = 0; i < tracks.length; i += BATCH_SIZE) {
        const batch = tracks.slice(i, i + BATCH_SIZE);

        await Promise.all(
          batch.map(async (track) => {
            // IMPORTANT: Must use trimmed values to match hash generation in other parts of the app
            const trackId = sha256(
              JSON.stringify({
                artist: track.Artist?.trim() ?? '',
                title: track.Title?.trim() ?? '',
              }),
            ).toString();

            if (matcher.trackMatches[trackId]?.length) return;

            try {
              const raw = await dispatch(
                getTrackMatches({
                  id: trackId,
                  artist: track.Artist,
                  title: track.Title,
                  count: 3,
                  bpm: track.BPM,
                  durationMs: toDurationMs(track.duration),
                }),
              ).unwrap();

              if (raw) {
                matchesToUpdate[trackId] = raw;
              }
            } catch (error) {
              console.warn(
                `Failed to get matches for ${track.Artist} - ${track.Title}`,
              );
            }
          }),
        );

        if (
          Object.keys(matchesToUpdate).length >= BATCH_SIZE ||
          i + BATCH_SIZE >= tracks.length
        ) {
          Object.entries(matchesToUpdate).forEach(([trackId, files]) => {
            dispatch(setTrackMatches({ trackId, files }));
          });

          Object.keys(matchesToUpdate).forEach(
            (key) => delete matchesToUpdate[key],
          );

          await new Promise((resolve) => setTimeout(resolve, 50));

          dispatch(
            setBulkProgress({
              total: tracks.length,
              completed: Math.min(i + BATCH_SIZE, tracks.length),
              mode,
              active: true,
            }),
          );
        }
      }

      // Apply selections
      if (mode === 'first') dispatch(selectFirstMatches());
      else if (mode === 'all') dispatch(selectAllMatches());
      else if (mode === 'mostPlayed') {
        let playCounts = seratoPlayCounts || {};
        try {
          if (Object.keys(playCounts).length === 0 && window.api) {
            console.log(
              '[trackMatcher] Play counts empty, fetching fresh ones... (This may take a while for large databases)',
            );

            // Fetch from both Serato and VirtualDJ
            run('serato/get-all-play-counts');
            run('virtualdj/get-all-play-counts');

            const timeoutPromise = new Promise((resolve) =>
              setTimeout(() => resolve(null), 30000),
            );

            const seratoFetchPromise = oncePromise<
              Record<string, { playCount: number }>
            >('serato/get-all-play-counts');

            const vdjFetchPromise = oncePromise<
              Record<string, { playCount: number }>
            >('virtualdj/get-all-play-counts');

            const [freshSeratoCounts, freshVdjCounts] = await Promise.all([
              Promise.race([seratoFetchPromise, timeoutPromise]),
              Promise.race([vdjFetchPromise, timeoutPromise]),
            ]);

            const mergedCounts = mergePlayCounts(
              freshSeratoCounts as Record<string, { playCount: number }>,
              freshVdjCounts as Record<string, { playCount: number }>,
            );

            if (Object.keys(mergedCounts).length > 0) {
              playCounts = mergedCounts;
            } else {
              console.warn(
                '[trackMatcher] Fetched play counts were empty or timed out',
              );
            }
          }
        } catch (e) {
          console.error('Failed to fetch fresh play counts', e);
        }
        dispatch(trackMatcherSlice.actions.selectMostPlayedMatches(playCounts));
      } else dispatch(selectNoMatches());

      // Save
      if (crateId) {
        const state = getState();
        run('crates/save-track-file-paths', {
          crate_id: crateId,
          filePaths: state.trackMatcherState.selectedMatches,
        });
      }
    } finally {
      dispatch(
        setBulkProgress({
          total: tracks.length,
          completed: tracks.length,
          mode,
          active: false,
        }),
      );
      activeBulkOperations.delete(operationKey);
    }
  },
);

export const loadAllSavedSelections = createAsyncThunk<
  void,
  { crateId: string; trackIds: string[] },
  { dispatch: AppDispatch; state: RootState }
>(
  'trackMatcher/loadAllSavedSelections',
  async ({ crateId, trackIds }, { dispatch }) => {
    for (const trackId of trackIds) {
      // this will write into state.selectedMatches[trackId]
      dispatch(getSavedSelectedTrackMatches({ crateId, trackId }));
    }
  },
);
export default trackMatcherSlice.reducer;
